/**
 * API functions for SharePoint Jobs management
 */

import { apiRequest } from "./api";

export interface SharePointJob {
  partitionKey: string;
  rowKey: string;
  timestamp?: Date;
  etag?: string;
  id: string;
  name: string;
  description?: string;
  isEnabled: boolean;
  dryRun?: boolean;
  schedule?: string;
  scheduleDescription?: string;
  sites: SharePointSiteConfig[];
  managementTaskId?: string;
  createdBy?: string;
  createdOn?: string;
  modifiedBy?: string;
  modifiedOn?: string;
}

/**
 * Email recipient for notifications
 */
export interface EmailRecipient {
  email: string;
  name?: string;
}

/**
 * Notification thresholds for quality alerts
 */
export interface NotificationThresholds {
  minPassRate?: number;
  maxFailCount?: number;
  alwaysSendSummary?: boolean;
  notifyOnJobFailure?: boolean;
}

/**
 * Notification configuration for a SharePoint site
 */
export interface SharePointNotificationConfig {
  enabled: boolean;
  fromAddress: string;
  recipients: EmailRecipient[];
  ccRecipients?: EmailRecipient[];
  thresholds?: NotificationThresholds;
  includeDetailedReport?: boolean;
}

export interface SharePointSiteConfig {
  siteId: string;
  driveId: string;
  columns: {
    width: string;
    height: string;
    dpiX: string;
    dpiY: string;
    fileSize: string;
    colorMode: string;
    processedDate: string;
    processingStatus: string;
    qualityStatus?: string;
    qualityScore?: string;
    qualityFlags?: string;
    recommendedAction?: string;
    altText?: string;
    altTextConfidence?: string;
    altTextGeneratedDate?: string;
    altTextTokensUsed?: string;
  };
  maxItemsPerRun?: number;
  folderPath?: string;
  qualityConfig?: {
    minDPI: number;
    preferredDPI: number;
    expectedColorMode?: 'RGB' | 'Grayscale' | 'CMYK';
    minWidth?: number;
    minHeight?: number;
    maxFileSize?: number;
  };
  folderScanConfig?: {
    recursive?: boolean;
    includePatterns?: string[];
    excludePatterns?: string[];
    maxDepth?: number;
  };
  enableAltText?: boolean;
  projectCode?: string;
  generateReport?: boolean;
  reportFolder?: string;
  forceReprocess?: boolean;
  notifications?: SharePointNotificationConfig;
}

export interface ResolvedSharePointSite {
  inputUrl: string;
  parsed: {
    hostname: string;
    sitePath: string;
    libraryHint?: string;
  };
  site: {
    id: string;
    displayName?: string;
    name?: string;
    webUrl?: string;
  };
  drives: Array<{
    id: string;
    name?: string;
    webUrl?: string;
    driveType?: string;
  }>;
  suggestedDriveId?: string;
  nextLink?: string;
}

/**
 * Fetch all SharePoint jobs
 */
export async function fetchSharePointJobs(): Promise<SharePointJob[]> {
  const res = await apiRequest("GET", "/api/sharepoint-jobs");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch SharePoint jobs");
  }
  const data = await res.json();
  return data.jobs || [];
}

/**
 * Resolve a SharePoint URL and one native page of document libraries.
 */
export async function resolveSharePointUrl(url: string, nextLink?: string): Promise<ResolvedSharePointSite> {
  const params = new URLSearchParams({ url });
  if (nextLink) params.set('nextLink', nextLink);
  const res = await apiRequest(
    "GET",
    `/api/sharepoint-picker/resolve?${params}`
  );
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to resolve SharePoint URL");
  }
  return res.json();
}

/**
 * Fetch a specific SharePoint job by ID
 */
export async function fetchSharePointJob(jobId: string): Promise<SharePointJob> {
  const res = await apiRequest("GET", `/api/sharepoint-jobs/${encodeURIComponent(jobId)}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch SharePoint job");
  }
  return res.json();
}

/**
 * Create a new SharePoint job
 */
export async function createSharePointJob(job: Partial<SharePointJob>): Promise<SharePointJob> {
  const res = await apiRequest("POST", "/api/sharepoint-jobs", job);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to create SharePoint job");
  }
  return res.json();
}

/**
 * Update an existing SharePoint job
 */
export async function updateSharePointJob(jobId: string, job: Partial<SharePointJob>): Promise<SharePointJob> {
  const res = await apiRequest("PUT", `/api/sharepoint-jobs/${encodeURIComponent(jobId)}`, job);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to update SharePoint job");
  }
  return res.json();
}

/**
 * Delete a SharePoint job
 */
export async function deleteSharePointJob(jobId: string): Promise<void> {
  const res = await apiRequest("DELETE", `/api/sharepoint-jobs/${encodeURIComponent(jobId)}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to delete SharePoint job");
  }
}

/**
 * Quality statistics for a SharePoint job
 */
export interface QualityStatistics {
  jobId: string;
  jobName: string;
  totals: {
    total: number;
    pass: number;
    warning: number;
    fail: number;
    notProcessed: number;
  };
  sites: Array<{
    siteId: string;
    stats: {
      total: number;
      pass: number;
      warning: number;
      fail: number;
      notProcessed: number;
    };
  }>;
}

export interface SharePointImageIndexRow {
  SiteId: string;
  DriveId: string;
  ItemId: string;
  SharePointJobId?: string | null;
  JobId?: string | null;
  CorrelationId?: string | null;
  ExecutionId?: string | null;
  FileName: string;
  FilePath?: string | null;
  FolderPath?: string | null;
  WebUrl?: string | null;
  FileSizeBytes?: number | null;
  Status: string;
  DryRun: boolean;
  DiscoveredAtUtc?: string | null;
  EnqueuedAtUtc?: string | null;
  ProcessedAtUtc?: string | null;
  LastSeenAtUtc: string;
  Width?: number | null;
  Height?: number | null;
  DpiX?: number | null;
  DpiY?: number | null;
  ColorMode?: string | null;
  ErrorMessage?: string | null;
  // Alt text fields
  AltText?: string | null;
  AltTextConfidence?: number | null;
  AltTextGeneratedDate?: string | null;
  AltTextTokensUsed?: number | null;
  AltTextModel?: string | null;
  AltTextError?: string | null;
}

// ============================================================================
// Alt Text API Types
// ============================================================================

/**
 * OpenRouter model configuration
 */
export interface AltTextModel {
  id: string;
  name: string;
  description: string;
  maxTokens: number;
  inputCostPer1M: number;
  outputCostPer1M: number;
}

/**
 * Alt text configuration response
 */
export interface AltTextConfig {
  models: AltTextModel[];
  defaultModel: string;
  defaultPrompt: string;
}

/**
 * Alt text generation request
 */
export interface GenerateAltTextRequest {
  model?: string;
  prompt?: string;
  updateSharePoint?: boolean;
}

/**
 * Alt text generation response
 */
export interface GenerateAltTextResponse {
  success: boolean;
  altText?: string;
  confidence?: number;
  tokensUsed?: number;
  model?: string;
  error?: string;
}

export async function searchSharePointImages(params: {
  folder?: string;
  fileName?: string;
  status?: string;
  siteId?: string;
  driveId?: string;
  sharePointJobId?: string;
  dryRun?: boolean;
  hasAltText?: boolean;
  limit?: number;
  offset?: number;
}): Promise<{ total: number; limit: number; offset: number; items: SharePointImageIndexRow[] }> {
  const qs = new URLSearchParams();
  if (params.folder) qs.set('folder', params.folder);
  if (params.fileName) qs.set('fileName', params.fileName);
  if (params.status) qs.set('status', params.status);
  if (params.siteId) qs.set('siteId', params.siteId);
  if (params.driveId) qs.set('driveId', params.driveId);
  if (params.sharePointJobId) qs.set('sharePointJobId', params.sharePointJobId);
  if (typeof params.dryRun === 'boolean') qs.set('dryRun', String(params.dryRun));
  if (typeof params.hasAltText === 'boolean') qs.set('hasAltText', String(params.hasAltText));
  if (typeof params.limit === 'number') qs.set('limit', String(params.limit));
  if (typeof params.offset === 'number') qs.set('offset', String(params.offset));

  const res = await apiRequest('GET', `/api/sharepoint-images?${qs.toString()}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || 'Failed to search SharePoint images');
  }
  return res.json();
}

export async function listSharePointImageFolders(params?: {
  prefix?: string;
  limit?: number;
}): Promise<{ limit: number; folders: Array<{ FolderPath: string; imageCount: number; lastActivityUtc: string }> }> {
  const qs = new URLSearchParams();
  if (params?.prefix) qs.set('prefix', params.prefix);
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));

  const res = await apiRequest('GET', `/api/sharepoint-images/folders?${qs.toString()}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || 'Failed to list SharePoint image folders');
  }
  return res.json();
}

/**
 * Fetch quality statistics for a SharePoint job
 */
export async function fetchQualityStatistics(jobId: string): Promise<QualityStatistics> {
  const res = await apiRequest("GET", `/api/sharepoint-jobs/${encodeURIComponent(jobId)}/quality-stats`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch quality statistics");
  }
  return res.json();
}

/**
 * Manual trigger result
 */
export interface TriggerJobResult {
  message: string;
  jobId: string;
  jobName: string;
  taskId: string;
  status: string;
}

/**
 * Manually trigger a SharePoint job to run immediately
 */
export async function triggerSharePointJob(jobId: string): Promise<TriggerJobResult> {
  const res = await apiRequest("POST", `/api/sharepoint-jobs/${encodeURIComponent(jobId)}/trigger`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to trigger job");
  }
  return res.json();
}

// ============================================================================
// Dashboard API Functions
// ============================================================================

/**
 * Dashboard summary response
 */
export interface DashboardSummary {
  jobs: {
    total: number;
    enabled: number;
    disabled: number;
    withSchedule: number;
  };
  quality: {
    totalProcessed: number;
    pass: number;
    warning: number;
    fail: number;
    notProcessed: number;
    passRate: number;
  };
  sites: {
    total: number;
    uniqueSiteIds: string[];
  };
  recentActivity: {
    lastExecution?: string;
    executionsLast24h: number;
    executionsLast7d: number;
  };
}

/**
 * Aggregate statistics response
 */
export interface AggregateStatistics {
  totals: {
    total: number;
    pass: number;
    warning: number;
    fail: number;
    notProcessed: number;
    passRate: number;
  };
  jobs: Array<{
    jobId: string;
    jobName: string;
    isEnabled: boolean;
    siteCount: number;
    quality: {
      total: number;
      pass: number;
      warning: number;
      fail: number;
      notProcessed: number;
      passRate: number;
    };
  }>;
}

/**
 * Recent activity response
 */
export interface RecentActivity {
  metrics: {
    lastExecution?: string;
    executionsLast24h: number;
    executionsLast7d: number;
  };
  recentExecutions: Array<{
    jobId: string;
    jobName: string;
    executedAt: string;
    status: string;
    imagesProcessed?: number;
    duration?: number;
  }>;
}

/**
 * Bulk operation result
 */
export interface BulkOperationResult {
  action: 'enable' | 'disable' | 'trigger';
  results: Array<{
    jobId: string;
    jobName?: string;
    success: boolean;
    taskId?: string;
    error?: string;
  }>;
  summary: {
    total: number;
    succeeded: number;
    failed: number;
  };
}

/**
 * Fetch dashboard summary with all statistics
 */
export async function fetchDashboardSummary(): Promise<DashboardSummary> {
  const res = await apiRequest("GET", "/api/sharepoint-dashboard");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch dashboard summary");
  }
  return res.json();
}

/**
 * Fetch aggregate quality statistics across all jobs
 */
export async function fetchAggregateStatistics(): Promise<AggregateStatistics> {
  const res = await apiRequest("GET", "/api/sharepoint-dashboard/statistics");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch aggregate statistics");
  }
  return res.json();
}

/**
 * Fetch recent activity and executions
 */
export async function fetchRecentActivity(): Promise<RecentActivity> {
  const res = await apiRequest("GET", "/api/sharepoint-dashboard/activity");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch recent activity");
  }
  return res.json();
}

/**
 * Bulk enable multiple jobs
 */
export async function bulkEnableJobs(jobIds: string[]): Promise<BulkOperationResult> {
  const res = await apiRequest("POST", "/api/sharepoint-dashboard/bulk-enable", { jobIds });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to enable jobs");
  }
  return res.json();
}

/**
 * Bulk disable multiple jobs
 */
export async function bulkDisableJobs(jobIds: string[]): Promise<BulkOperationResult> {
  const res = await apiRequest("POST", "/api/sharepoint-dashboard/bulk-disable", { jobIds });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to disable jobs");
  }
  return res.json();
}

/**
 * Bulk trigger multiple jobs to run immediately
 */
export async function bulkTriggerJobs(jobIds: string[]): Promise<BulkOperationResult> {
  const res = await apiRequest("POST", "/api/sharepoint-dashboard/bulk-trigger", { jobIds });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to trigger jobs");
  }
  return res.json();
}

// ============================================================================
// Column Validation API Functions
// ============================================================================

/**
 * Result from column validation/provisioning
 */
export interface ColumnValidationResult {
  success: boolean;
  listId: string;
  existingColumns: Array<{ name: string; type: string }>;
  createdColumns: Array<{ name: string; type: string }>;
  failedColumns: Array<{ name: string; error: string }>;
  message: string;
  error?: string;
  details?: string;
}

/**
 * Validate and provision columns for a SharePoint site configuration
 * This checks if required columns exist and creates any missing ones
 */
export async function validateAndProvisionColumns(
  siteId: string,
  driveId: string,
  columns: SharePointSiteConfig['columns']
): Promise<ColumnValidationResult> {
  const res = await apiRequest("POST", "/api/sharepoint-jobs/validate-columns", {
    siteId,
    driveId,
    columns
  });

  // Handle both success and partial success (207)
  if (!res.ok && res.status !== 207) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || error?.details || "Failed to validate columns");
  }

  return res.json();
}

// ============================================================================
// Alt Text API Functions
// ============================================================================

/**
 * Get alt text configuration (models and default prompt)
 */
export async function getAltTextConfig(): Promise<AltTextConfig> {
  const res = await apiRequest("GET", "/api/sharepoint-images/alt-text-config");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to get alt text configuration");
  }
  return res.json();
}

/**
 * Get thumbnail URL for an image
 * Returns a URL that can be used as img src
 */
export function getThumbnailUrl(siteId: string, driveId: string, itemId: string): string {
  const baseUrl = import.meta.env.VITE_API_URL || '';
  return `${baseUrl}/api/sharepoint-images/${encodeURIComponent(siteId)}/${encodeURIComponent(driveId)}/${encodeURIComponent(itemId)}/thumbnail`;
}

/**
 * Generate alt text for a specific image
 */
export async function generateImageAltText(
  siteId: string,
  driveId: string,
  itemId: string,
  options?: GenerateAltTextRequest
): Promise<GenerateAltTextResponse> {
  const res = await apiRequest(
    "POST",
    `/api/sharepoint-images/${encodeURIComponent(siteId)}/${encodeURIComponent(driveId)}/${encodeURIComponent(itemId)}/generate-alt-text`,
    options || {}
  );

  // Don't throw on 500 - return the error response instead for better UX
  const data = await res.json();
  return data;
}
